import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AppLayout from './layouts/AppLayout';
import Pipeline from './routes/Pipeline';
import Profile from './routes/Profile';
import Requests from './routes/Requests';
import Settings from './routes/Settings';
import { api, getMe, type User } from './lib/api';

const AuthScreen: React.FC<{ onAuth: (user: User) => void }> = ({ onAuth }) => {
  const [mode, setMode] = useState<'login' | 'setup'>('login');
  const [email, setEmail] = useState('suhaibabdeljaber@gmail.com');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('123456');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      if (mode === 'setup') {
        await api<User>('/api/auth/setup', {
          method: 'POST',
          body: JSON.stringify({ email, password, token })
        });
      }
      const user = await api<User>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      });
      onAuth(user);
    } catch (err: any) {
      setError(err?.message || 'Authentication failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="authPage">
      <section className="authIntro">
        <span className="eyebrow">Private tool</span>
        <h1>Find exact source URLs. Draft broker removals. Send when ready.</h1>
        <p>
          One private workflow: save your details, run a deep index, verify source URLs, review drafts, and send prepared removal requests.
        </p>
        <div className="authFlow">
          <div><strong>01</strong><span>Profile</span></div>
          <div><strong>02</strong><span>Deep index</span></div>
          <div><strong>03</strong><span>Verify + draft</span></div>
          <div><strong>04</strong><span>Send</span></div>
        </div>
      </section>

      <section className="authPanel">
        <div className="segmented" aria-label="Auth mode">
          <button type="button" className={mode === 'login' ? 'selected' : ''} onClick={() => setMode('login')}>Login</button>
          <button type="button" className={mode === 'setup' ? 'selected' : ''} onClick={() => setMode('setup')}>Setup</button>
        </div>
        <h2>{mode === 'login' ? 'Open privacy pipeline' : 'Create owner account'}</h2>
        <form onSubmit={submit} className="formStack">
          <label>Email<input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required /></label>
          <label>Password<input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required minLength={8} /></label>
          {mode === 'setup' && <label>Setup token<input value={token} onChange={(e) => setToken(e.target.value)} required /></label>}
          {error && <div className="notice danger">{error}</div>}
          <button type="submit" className="primaryButton full" disabled={busy}>{busy ? 'Working...' : mode === 'login' ? 'Login' : 'Create and login'}</button>
        </form>
      </section>
    </main>
  );
};

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const storedTheme = localStorage.getItem('theme');
    document.documentElement.dataset.theme = storedTheme === 'dark' ? 'dark' : 'light';
    getMe().then(setUser).finally(() => setChecking(false));
  }, []);

  async function logout() {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    setUser(null);
  }

  if (checking) return <div className="loadingScreen"><div className="loadingCard">Loading privacy pipeline...</div></div>;
  if (!user) return <AuthScreen onAuth={setUser} />;

  return (
    <AppLayout user={user} onLogout={logout}>
      <Routes>
        <Route path="/" element={<Navigate to="/pipeline" />} />
        <Route path="/pipeline" element={<Pipeline />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/requests" element={<Requests />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/pipeline" />} />
      </Routes>
    </AppLayout>
  );
}
